import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createCheckout,
  createCreditCheckout,
  getBillingPlans,
  getCredit,
  getCreditTransactions,
  getCreditUsage,
  getCurrentSubscription,
  getInvoices,
  getPayments,
} from '@/services/billingService.js';
import { QUERY_KEYS } from '@/shared/constant/queryKeys.js';

export function usePlans() {
  return useQuery({
    queryKey: QUERY_KEYS.BILLING_PLANS,
    queryFn: getBillingPlans,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSubscription(tenantId) {
  return useQuery({
    queryKey: QUERY_KEYS.BILLING_SUBSCRIPTION(tenantId),
    queryFn: () => getCurrentSubscription(tenantId),
    enabled: Boolean(tenantId),
    retry: false,
  });
}

export function useInvoices(tenantId) {
  return useQuery({
    queryKey: QUERY_KEYS.BILLING_INVOICES(tenantId),
    queryFn: () => getInvoices(tenantId),
    enabled: Boolean(tenantId),
  });
}

export function usePayments(tenantId) {
  return useQuery({
    queryKey: QUERY_KEYS.BILLING_PAYMENTS(tenantId),
    queryFn: () => getPayments(tenantId),
    enabled: Boolean(tenantId),
  });
}

export function useCreditSummary(tenantId) {
  return useQuery({
    queryKey: QUERY_KEYS.BILLING_CREDIT(tenantId),
    queryFn: () => getCredit(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 30 * 1000,
  });
}

export function useCreditTransactions(tenantId) {
  return useQuery({
    queryKey: QUERY_KEYS.BILLING_CREDIT_TRANSACTIONS(tenantId),
    queryFn: () => getCreditTransactions(tenantId),
    enabled: Boolean(tenantId),
  });
}

export function useCreditUsage(tenantId, { days, page, pageSize }) {
  return useQuery({
    queryKey: QUERY_KEYS.BILLING_CREDIT_USAGE(tenantId, days, page, pageSize),
    queryFn: () => getCreditUsage({ tenantId, days, page, pageSize }),
    enabled: Boolean(tenantId),
    staleTime: 30 * 1000,
  });
}

export function usePlanCheckout(tenantId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCheckout,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BILLING_INVOICES(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BILLING_PAYMENTS(tenantId),
      });
    },
  });
}

export function useCreditCheckout(tenantId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCreditCheckout,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BILLING_INVOICES(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BILLING_PAYMENTS(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BILLING_CREDIT(tenantId),
      });
    },
  });
}
